import React, { useEffect, useMemo, useState } from 'react';
import { Users, MapPin, UserPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { playerApi } from '../../../utils/api';
import { supabase } from '../../../lib/supabase';
import { useTeamStore } from '../../../store/teamStore';
import type { Player } from '../../../types';
import type { TennisMatchType } from '../../../types';
import { TENNIS_MATCH_FORMATS, DEFAULT_TENNIS_MATCH_FORMAT } from '../../../data/tennisMatchFormats';

const SINGLES_LADDER = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'];
const DOUBLES_LADDER = ['D1', 'D2', 'D3'];
const LADDER_RANK: Record<string, number> = Object.fromEntries(
  [...SINGLES_LADDER, ...DOUBLES_LADDER].map((key, index) => [key, index])
);

export interface TennisMatchSetupValue {
  matchType: TennisMatchType;
  /** 1 entry for Singles, 2 for Doubles/Mixed Doubles. */
  playerIds: string[];
  /** '' = no ladder slot recorded for this match. */
  ladderSlot: string;
  /** 1 entry for Singles, 2 for Doubles/Mixed Doubles — joined into opponent_name on submit. */
  opponentNames: string[];
  opponentSchool: string;
  court: string;
  formatId: string;
  noAd: boolean;
}

export const DEFAULT_TENNIS_MATCH_SETUP: TennisMatchSetupValue = {
  matchType: 'Singles',
  playerIds: [''],
  ladderSlot: '',
  opponentNames: [''],
  opponentSchool: '',
  court: '',
  formatId: DEFAULT_TENNIS_MATCH_FORMAT.id,
  noAd: DEFAULT_TENNIS_MATCH_FORMAT.noAd,
};

/** How many of our players this match type needs on our side. */
function slotsForType(matchType: TennisMatchType): number {
  return matchType === 'Singles' ? 1 : 2;
}

interface TennisMatchSetupFieldsProps {
  teamId: string;
  value: TennisMatchSetupValue;
  onChange: (value: TennisMatchSetupValue) => void;
}

/**
 * Tennis-only fields for CreateGameWizard: match type, player(s), optional
 * ladder slot, opponent school, court, and a format preset + no-ad toggle.
 * Kept as its own component so CreateGameWizard doesn't grow a second
 * sport-specific block on top of the existing baseball lineup one.
 */
export const TennisMatchSetupFields: React.FC<TennisMatchSetupFieldsProps> = ({ teamId, value, onChange }) => {
  const { currentTeam } = useTeamStore();
  const isPersonal = !!currentTeam?.isPersonal;
  const [roster, setRoster] = useState<Player[]>([]);
  const [ladderRankByPlayerId, setLadderRankByPlayerId] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [partnerName, setPartnerName] = useState('');
  const [addingPartner, setAddingPartner] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [playersResult, depthChartResult] = await Promise.all([
        playerApi.getByTeam(teamId),
        supabase
          .from('depth_chart')
          .select('position, depth, player_id')
          .eq('team_id', teamId)
          .eq('unit', 'Positions')
          .eq('depth', 1),
      ]);
      if (cancelled) return;

      if (playersResult.success && playersResult.data) {
        setRoster(playersResult.data);
      }

      const rankMap = new Map<string, number>();
      for (const row of depthChartResult.data ?? []) {
        if (row.player_id && row.position in LADDER_RANK) {
          rankMap.set(row.player_id, LADDER_RANK[row.position]);
        }
      }
      setLadderRankByPlayerId(rankMap);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const sortedRoster = useMemo(() => {
    return [...roster].sort((a, b) => {
      const rankA = ladderRankByPlayerId.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const rankB = ladderRankByPlayerId.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      if (rankA !== rankB) return rankA - rankB;
      return `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`);
    });
  }, [roster, ladderRankByPlayerId]);

  // An individual (parent/player) account's roster is just that one athlete — no reason to
  // make them pick themselves from a dropdown every time. Doesn't lock the field; they can
  // still change it if the roster ever grows beyond one player.
  useEffect(() => {
    if (!isPersonal || loading || value.playerIds[0] || sortedRoster.length === 0) return;
    const nextPlayerIds = [...value.playerIds];
    nextPlayerIds[0] = sortedRoster[0].id;
    onChange({ ...value, playerIds: nextPlayerIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-check when these specific things change, not on every `value`/`onChange` identity change
  }, [isPersonal, loading, sortedRoster, value.playerIds[0]]);

  /**
   * Individual accounts have a one-player roster, so there's no existing doubles
   * partner to pick from a dropdown — typing a name and adding them to the roster
   * is the only way to field a doubles pair. Mirrors Welcome.tsx's minimal insert
   * shape for creating this account's very first player.
   */
  const handleAddPartner = async () => {
    const name = partnerName.trim();
    if (!name) return;
    setAddingPartner(true);
    try {
      const [firstName, ...rest] = name.split(/\s+/);
      const lastName = rest.join(' ');
      const { data: newPlayer, error } = await supabase
        .from('players')
        .insert({
          team_id: teamId,
          first_name: firstName,
          last_name: lastName,
          position: 'D1',
          experience: 'Intermediate',
        })
        .select()
        .single();

      if (error || !newPlayer) throw error ?? new Error('No player returned');

      setRoster((prev) => [...prev, newPlayer]);
      handlePlayerChange(1, newPlayer.id);
      setPartnerName('');
      toast.success(`${newPlayer.first_name} added`);
    } catch (error) {
      console.error('[TennisMatchSetupFields] Error adding doubles partner:', error);
      toast.error('Failed to add partner');
    } finally {
      setAddingPartner(false);
    }
  };

  const ladderOptions = value.matchType === 'Singles' ? SINGLES_LADDER : DOUBLES_LADDER;
  const slotsNeeded = slotsForType(value.matchType);

  const handleTypeChange = (matchType: TennisMatchType) => {
    const nextSlots = slotsForType(matchType);
    onChange({
      ...value,
      matchType,
      playerIds: Array.from({ length: nextSlots }, (_, i) => value.playerIds[i] ?? ''),
      opponentNames: Array.from({ length: nextSlots }, (_, i) => value.opponentNames[i] ?? ''),
      // The player's previous ladder slot rarely applies across type changes (S-series vs D-series).
      ladderSlot: '',
    });
  };

  const handlePlayerChange = (index: number, playerId: string) => {
    const nextPlayerIds = [...value.playerIds];
    nextPlayerIds[index] = playerId;
    onChange({ ...value, playerIds: nextPlayerIds });
  };

  const handleOpponentNameChange = (index: number, name: string) => {
    const nextNames = [...value.opponentNames];
    nextNames[index] = name;
    onChange({ ...value, opponentNames: nextNames });
  };

  const handleFormatChange = (formatId: string) => {
    const format = TENNIS_MATCH_FORMATS.find((f) => f.id === formatId) ?? DEFAULT_TENNIS_MATCH_FORMAT;
    onChange({ ...value, formatId, noAd: format.noAd });
  };

  return (
    <div className="space-y-6">
      {/* Match type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Match Type <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          {(['Singles', 'Doubles', 'Mixed Doubles'] as TennisMatchType[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => handleTypeChange(type)}
              className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${
                value.matchType === type
                  ? 'border-primary bg-primary/5 text-primary font-medium'
                  : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Our player(s) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Users className="inline w-4 h-4 mr-2" />
          {slotsNeeded === 1 ? 'Player' : 'Players'} <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {Array.from({ length: slotsNeeded }, (_, i) => {
            // Individual account's doubles partner: a 1-player roster has no one else to pick
            // from a dropdown, so type a name and add them to the roster instead.
            if (isPersonal && i === 1) {
              const selectedPartner = sortedRoster.find((p) => p.id === value.playerIds[1]);
              if (selectedPartner) {
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                    <span className="text-sm text-gray-900">
                      {selectedPartner.first_name} {selectedPartner.last_name}
                    </span>
                    <button type="button" onClick={() => handlePlayerChange(1, '')} className="text-xs text-primary hover:underline">
                      Change
                    </button>
                  </div>
                );
              }
              return (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={partnerName}
                    onChange={(e) => setPartnerName(e.target.value)}
                    placeholder="Partner's name"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={handleAddPartner}
                    disabled={addingPartner || !partnerName.trim()}
                    className="btn-secondary px-3 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <UserPlus className="w-4 h-4" />
                    {addingPartner ? 'Adding...' : 'Add'}
                  </button>
                </div>
              );
            }
            return (
              <select
                key={i}
                value={value.playerIds[i] ?? ''}
                onChange={(e) => handlePlayerChange(i, e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50"
                required
              >
                <option value="">{loading ? 'Loading roster...' : `Select player ${slotsNeeded > 1 ? i + 1 : ''}`}</option>
                {sortedRoster.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.first_name} {player.last_name}
                  </option>
                ))}
              </select>
            );
          })}
        </div>
      </div>

      {/* Ladder slot (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Ladder Slot (Optional)</label>
        <select
          value={value.ladderSlot}
          onChange={(e) => onChange({ ...value, ladderSlot: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="">Not set</option>
          {ladderOptions.map((slot) => (
            <option key={slot} value={slot}>{slot}</option>
          ))}
        </select>
      </div>

      {/* Opponent name(s) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Opponent Name{slotsNeeded > 1 ? 's' : ''} <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {Array.from({ length: slotsNeeded }, (_, i) => (
            <input
              key={i}
              type="text"
              value={value.opponentNames[i] ?? ''}
              onChange={(e) => handleOpponentNameChange(i, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder={slotsNeeded > 1 ? `Opponent ${i + 1} name` : 'Opponent name'}
              required
            />
          ))}
        </div>
      </div>

      {/* Opponent school (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Opponent School (Optional)</label>
        <input
          type="text"
          value={value.opponentSchool}
          onChange={(e) => onChange({ ...value, opponentSchool: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="Enter opponent school or club"
        />
      </div>

      {/* Court (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <MapPin className="inline w-4 h-4 mr-2" />
          Court (Optional)
        </label>
        <input
          type="text"
          value={value.court}
          onChange={(e) => onChange({ ...value, court: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          placeholder="e.g. Court 3"
        />
      </div>

      {/* Format preset + no-ad toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Match Format</label>
        <select
          value={value.formatId}
          onChange={(e) => handleFormatChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent mb-3"
        >
          {TENNIS_MATCH_FORMATS.map((format) => (
            <option key={format.id} value={format.id}>{format.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={value.noAd}
            onChange={(e) => onChange({ ...value, noAd: e.target.checked })}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-sm text-gray-700">No-ad scoring (sudden death at deuce)</span>
        </label>
      </div>
    </div>
  );
};

export default TennisMatchSetupFields;
